import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { loadLessons } from '../../../Redux/lessonsSlice';
import { loadStudents } from '../../../Redux/studentsSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import type { LessonModel } from '../../../Models/LessonModel';
import type { StudentModel, StudentSlotModel } from '../../../Models/StudentModel';
import { LessonService } from '../../../Services/LessonService';
import { StudentService } from '../../../Services/StudentService';
import { GoogleCalendarService } from '../../../Services/GoogleCalendarService';
import { lessonChangesFromEvent, slotChangesFromEvent, type CalendarEvent } from '../../../Utils/GoogleCalendarSync';
import { isCancelled, localDateString } from '../../../Utils/LessonStatusUtil';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import './GoogleCalendar.css';

/**
 * Two-way sync with the tutor's own Google Calendar.
 *
 * Two rules, both carried over because both were learned the hard way:
 *
 *   The calendar wins. A lesson moved in Google Calendar is a lesson that
 *   moved, and Magbit disagreeing with what the tutor sees on their phone is
 *   the failure worth designing against.
 *
 *   Nothing is ever deleted automatically. A pull applies edits; a lesson that
 *   has vanished from the calendar is reported and left alone, because an
 *   event that failed to load must not empty a tutor's ledger.
 */
interface SlotWithStudent { student: string; slot: StudentSlotModel }

function eachSlot(students: readonly StudentModel[]): SlotWithStudent[] {
  return students.flatMap(student =>
    (student.slots ?? []).map(slot => ({ student: student.name, slot })));
}

const unlinkedSlots = (students: readonly StudentModel[]) =>
  eachSlot(students).filter(entry => !entry.slot.gcalEventId);

const linkedSlots = (students: readonly StudentModel[]) =>
  eachSlot(students).filter(entry => Boolean(entry.slot.gcalEventId));

export function GoogleCalendar() {
  const { tr } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.session.user);
  const lessons = useAppSelector(state => state.lessons.items);
  const students = useAppSelector(state => state.students.items);

  const [status, setStatus] = useState({ connected: false, email: null as string | null });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ updated: number; missing: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => GoogleCalendarService.onChange(setStatus), []);

  // A grant given earlier is still a grant. Without this the screen opens as
  // "not connected" every time and asks for consent again, and nothing in the
  // background can push an edit because there is no token to push it with.
  useEffect(() => { void GoogleCalendarService.ensureSilentToken(); }, []);

  async function connect() {
    setError(null);
    try {
      await GoogleCalendarService.connect();
    } catch {
      setError('SERVER_ERROR');
    }
  }

  /**
   * Pushes every lesson and recurring slot that has no event yet. Never
   * deletes. A cancelled lesson is skipped: it is a ledger entry, not an
   * appointment, and the automatic sync already removed its event.
   *
   * The count is reported even when the run fails part way. Pushing sixty
   * lessons and being told only "error" hides the fifty that did land, and
   * sends the tutor back to press the button again for work already done.
   */
  async function pushAll() {
    if (!user) return;
    setBusy(true);
    setError(null);
    let pushed = 0;
    try {
      for (const lesson of lessons.filter(candidate => !candidate.gcalEventId && !isCancelled(candidate))) {
        const eventId = await GoogleCalendarService.createLessonEvent(lesson, user.uid);
        if (eventId) {
          await LessonService.linkCalendarEvent(lesson.id, eventId);
          pushed += 1;
        }
      }
      for (const { student, slot } of unlinkedSlots(students)) {
        const eventId = await GoogleCalendarService.createSlotEvent({
          slotId: slot.id,
          student,
          weekday: Number(slot.weekday),
          time: slot.time,
          durationMinutes: Number(slot.durationMinutes)
        }, user.uid);
        if (eventId) {
          await StudentService.linkSlotCalendarEvent(slot.id, eventId);
          pushed += 1;
        }
      }
    } catch {
      setError('SERVER_ERROR');
    } finally {
      setResult({ updated: pushed, missing: 0 });
      void dispatch(loadLessons());
      void dispatch(loadStudents());
      setBusy(false);
    }
  }

  /** Pulls edits back. Deletions are counted and reported, never applied. */
  async function pull() {
    setBusy(true);
    setError(null);
    let updated = 0;
    let missing = 0;
    try {
      const linked = lessons.filter((lesson): lesson is LessonModel & { gcalEventId: string } =>
        Boolean(lesson.gcalEventId));
      const slots = linkedSlots(students);
      if (linked.length === 0 && slots.length === 0) {
        setResult({ updated: 0, missing: 0 });
        return;
      }

      /*
       * The bulk list is an optimisation, not the source of truth.
       *
       * It can only be asked for a date window, and the window can only be
       * built from where Magbit thinks the lessons are. An event the tutor
       * dragged to next month is then outside it and comes back missing,
       * which is exactly the move the sync exists to notice. So anything the
       * list does not account for is fetched by id, which has no window at
       * all, and only an event that is genuinely gone stays missing.
       */
      const dates = linked.map(lesson => lesson.date).sort();
      const byId = new Map<string | undefined, CalendarEvent>();
      if (dates.length > 0) {
        const events = await GoogleCalendarService.listMagbitEvents(
          dates[0] ?? localDateString(),
          dates[dates.length - 1] ?? localDateString()
        );
        for (const event of events) byId.set(event.id, event);
      }
      const names = new Set(students.map(student => student.name));

      for (const lesson of linked) {
        const event = byId.get(lesson.gcalEventId)
          ?? await GoogleCalendarService.getEvent(lesson.gcalEventId);
        if (!event) {
          missing += 1;
          continue;
        }
        const changes = lessonChangesFromEvent(event, lesson, names);
        if (Object.keys(changes).length === 0) continue;
        await LessonService.update(lesson.id, changes);
        updated += 1;
      }

      // Slots are always read by id: a weekly series is one event whose id
      // Magbit stored, and listing expands or filters it by date in ways that
      // do not answer "where is this series now".
      for (const { slot } of slots) {
        const event = await GoogleCalendarService.getEvent(slot.gcalEventId as string);
        if (!event) {
          missing += 1;
          continue;
        }
        const changes = slotChangesFromEvent(event, slot);
        if (Object.keys(changes).length === 0) continue;
        await StudentService.updateSlot(slot.id, changes);
        updated += 1;
      }
    } catch {
      setError('SERVER_ERROR');
    } finally {
      setResult({ updated, missing });
      void dispatch(loadLessons());
      void dispatch(loadStudents());
      setBusy(false);
    }
  }

  // Slots count too, because the push button sends both.
  const unlinked = lessons.filter(lesson => !lesson.gcalEventId && !isCancelled(lesson)).length
    + unlinkedSlots(students).length;

  return (
    <section className="google-calendar">
      <h1 className="screen-title">{tr('navGoogleCalendar')}</h1>

      <section className="profile-panel">
        <h2>{tr('gcalConnection')}</h2>
        {status.connected
          ? (
            <>
              <p className="profile-identity">
                <span className="profile-saved">{tr('gcalConnected')}</span>
                {status.email && <bdi className="profile-email">{status.email}</bdi>}
              </p>
              <button type="button" className="ghost-button" onClick={() => GoogleCalendarService.disconnect()}>
                {tr('gcalDisconnect')}
              </button>
            </>
          )
          : (
            <>
              <p className="dashboard-quiet">{tr('gcalNotConnected')}</p>
              <button type="button" className="primary-button" onClick={() => void connect()}>
                {tr('gcalConnect')}
              </button>
            </>
          )}
      </section>

      {status.connected && (
        <section className="profile-panel">
          <h2>{tr('gcalSyncTitle')}</h2>
          <ul className="marketing-rules gcal-rules">
            <li>{tr('syncRule1')}</li>
            <li>{tr('syncRule2')}</li>
            <li>{tr('syncRule3')}</li>
          </ul>
          <div className="backup-actions">
            <button type="button" className="primary-button" onClick={() => void pushAll()} disabled={busy}>
              {busy ? <Spinner inline /> : tr('gcalPush', unlinked)}
            </button>
            <button type="button" className="ghost-button" onClick={() => void pull()} disabled={busy}>
              {tr('gcalPull')}
            </button>
          </div>
          {result && (
            <p className="profile-saved">
              {tr('gcalSyncDone', result.updated)}
              {result.missing > 0 && ` · ${tr('gcalMissing', result.missing)}`}
            </p>
          )}
        </section>
      )}

      {error && <ErrorState code={error} />}
    </section>
  );
}
